import { useState, useEffect, useRef, createContext, useContext, ReactNode, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { BillingModel } from "@/lib/billing/types";
import { OrganizationBranding } from "@/lib/branding";

export type OrgRole = "owner" | "admin" | "trainer";

export interface Organization {
  id: string;
  name: string;
  slug: string;
  settings: {
    default_package_sizes: number[];
    pay_period: {
      reference_date: string;
      period_days: number;
    };
    branding: OrganizationBranding;
    features?: {
      google_sheets_sync?: boolean;
    };
    /** Billing model for this organization. Defaults to 'packages' if not set. */
    billing_model?: BillingModel;
    /**
     * How the org recognizes revenue from package sessions.
     * - 'per_session' (default): each completed session creates an earnings entry
     * - 'lump_sum': only re-up / package payment / gym tour / manual entries count;
     *   completing a session does NOT auto-create an earnings entry.
     * Tessa-style trainers want lump_sum because they bill upfront.
     */
    revenue_recognition_method?: "per_session" | "lump_sum";
  };
}

export interface OrganizationMembership {
  organization_id: string;
  org_role: OrgRole;
  organization: Organization;
}

interface OrganizationContextType {
  organization: Organization | null;
  orgRole: OrgRole | null;
  isOwner: boolean;
  isAdmin: boolean;
  isOrgAdminOrOwner: boolean;
  loading: boolean;
  allMemberships: OrganizationMembership[];
  hasMultipleOrgs: boolean;
  switchOrganization: (orgId: string) => void;
  refetch: () => Promise<void>;
  updateOrgSettings: (settings: Partial<Organization["settings"]>) => Promise<void>;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [orgRole, setOrgRole] = useState<OrgRole | null>(null);
  const [allMemberships, setAllMemberships] = useState<OrganizationMembership[]>([]);
  const [loading, setLoading] = useState(true);
  const currentUserIdRef = useRef<string | null>(null);
  const queryClient = useQueryClient();

  const fetchOrganization = useCallback(async () => {
    if (!user) {
      setOrganization(null);
      setOrgRole(null);
      setAllMemberships([]);
      currentUserIdRef.current = null;
      setLoading(false);
      return;
    }

    try {
      currentUserIdRef.current = user.id;

      // Fetch membership AND organization in a SINGLE query using join
      // User may be in multiple orgs, so we get all and prioritize owner role
      const { data: memberships, error } = await supabase
        .from("organization_members")
        .select(`
          org_role,
          organization_id,
          organizations!inner (
            id,
            name,
            slug,
            settings
          )
        `)
        .eq("user_id", user.id);

      if (error || !memberships || memberships.length === 0) {
        if (error) {
          console.error("Error fetching organization:", error);
        }
        setOrganization(null);
        setOrgRole(null);
        setAllMemberships([]);
        return;
      }

      // Prioritize: owner > admin > trainer
      const rolePriority: Record<string, number> = { owner: 0, admin: 1, trainer: 2 };
      const sortedMemberships = [...memberships].sort((a, b) => {
        const priorityA = rolePriority[a.org_role] ?? 99;
        const priorityB = rolePriority[b.org_role] ?? 99;
        return priorityA - priorityB;
      });

      // Build typed membership array
      const typedMemberships: OrganizationMembership[] = sortedMemberships.map((m) => ({
        organization_id: m.organization_id,
        org_role: m.org_role as OrgRole,
        organization: m.organizations as unknown as Organization,
      }));
      setAllMemberships(typedMemberships);

      // Check for a previously-selected org in localStorage
      const storageKey = `tp_selected_org_${user.id}`;
      const savedOrgId = localStorage.getItem(storageKey);
      const savedMembership = savedOrgId
        ? typedMemberships.find((m) => m.organization_id === savedOrgId)
        : null;

      const selected = savedMembership || typedMemberships[0];
      setOrganization(selected.organization);
      setOrgRole(selected.org_role);
    } catch (error) {
      console.error("Error in fetchOrganization:", error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const switchOrganization = useCallback((orgId: string) => {
    const membership = allMemberships.find((m) => m.organization_id === orgId);
    if (!membership) return;

    setOrganization(membership.organization);
    setOrgRole(membership.org_role);

    // Persist selection
    if (currentUserIdRef.current) {
      localStorage.setItem(`tp_selected_org_${currentUserIdRef.current}`, orgId);
    }

    // Force all queries to refetch with the new org context
    queryClient.clear();
  }, [allMemberships, queryClient]);

  const updateOrgSettings = async (newSettings: Partial<Organization["settings"]>) => {
    if (!organization) return;

    // Use RPC to surgically merge keys via jsonb_set, so keys not included
    // in this update (e.g. features) are preserved in the database.
    const { data: mergedSettings, error } = await supabase
      .rpc("merge_org_settings", {
        _org_id: organization.id,
        _new_settings: JSON.parse(JSON.stringify(newSettings)),
      });

    if (error) {
      throw error;
    }

    setOrganization({
      ...organization,
      settings: mergedSettings as Organization["settings"],
    });
  };

  useEffect(() => {
    fetchOrganization();
  }, [fetchOrganization]);

  const isOwner = orgRole === "owner";
  const isAdmin = orgRole === "admin";
  const isOrgAdminOrOwner = isOwner || isAdmin;

  return (
    <OrganizationContext.Provider
      value={{
        organization,
        orgRole,
        isOwner,
        isAdmin,
        isOrgAdminOrOwner,
        loading,
        allMemberships,
        hasMultipleOrgs: allMemberships.length > 1,
        switchOrganization,
        refetch: fetchOrganization,
        updateOrgSettings,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
}

export function useOrganization() {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error("useOrganization must be used within an OrganizationProvider");
  }
  return context;
}
